(() => {
  if (!('customElements' in window) || customElements.get('product-delivery')) return;

  const DAY_MS = 86400000;
  const MAX_LOOKAHEAD = 366;
  const TICK_MS = 30000;
  const CART_EVENTS = ['cart:updated', 'cart:change', 'cart:refresh', 'ajaxProduct:added'];
  const OFFSET_PATTERN = /^([+-])(\d{2}):?(\d{2})$/;
  const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
  const FORM_EXCLUDE = [
    '.complementary-products',
    '[data-complementary-products]',
    'product-recommendations',
    '[data-product-recommendations]',
    '.product-recommendations',
    'sticky-atc',
  ].join(',');

  const toInt = (value, fallback) => {
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : fallback;
  };
  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
  const addDays = (date, days) => new Date(date.getTime() + days * DAY_MS);
  const toIso = (date) => date.toISOString().slice(0, 10);
  const fill = (template, tokens) =>
    Object.entries(tokens).reduce((output, [key, value]) => output.split(`[${key}]`).join(value), template ?? '');

  const resolveLocale = () => {
    const candidate = document.documentElement.lang || window.Shopify?.locale;
    if (!candidate) return undefined;
    try {
      return Intl.getCanonicalLocales(candidate)[0];
    } catch {
      return undefined;
    }
  };

  class ProductDelivery extends HTMLElement {
    constructor() {
      super();
      this.initialized = false;
      this.cartTotal = null;
      this.tickTimer = null;
      this.variantTimer = null;
      this.cartTimer = null;
      this.cartController = null;
      this.moneyFormatters = new Map();
      this.render = this.render.bind(this);
      this.handleVariantSignal = this.handleVariantSignal.bind(this);
      this.handleDocumentVariant = this.handleDocumentVariant.bind(this);
      this.handleCartEvent = this.handleCartEvent.bind(this);
      this.handleVisibility = this.handleVisibility.bind(this);
    }

    connectedCallback() {
      if (this.initialized) return;

      const payload = this.parsePayload();
      this.strings = payload.strings ?? {};
      this.variants = payload.variants && typeof payload.variants === 'object' ? payload.variants : {};
      this.config = this.buildConfig(payload);
      this.locale = resolveLocale();
      this.dateFormatter = this.createDateFormatter();
      this.partsFormatter = this.createPartsFormatter();

      this.rows = {
        estimate: this.querySelector('[data-delivery-estimate]'),
        range: this.querySelector('[data-delivery-range]'),
        countdown: this.querySelector('[data-delivery-countdown]'),
        countdownText: this.querySelector('[data-delivery-countdown-text]'),
        free: this.querySelector('[data-delivery-free]'),
        freeText: this.querySelector('[data-delivery-free-text]'),
        digital: this.querySelector('[data-delivery-digital]'),
      };

      this.section =
        document.getElementById(`shopify-section-${this.dataset.sectionId}`) ||
        this.closest('.shopify-section');
      this.form = this.findForm();
      this.variantId = this.readVariantId();

      if (this.section) {
        this.section.addEventListener('change', this.handleVariantSignal, true);
        this.section.addEventListener('variant:change', this.handleVariantSignal, true);
      }
      document.addEventListener('variant:change', this.handleDocumentVariant);
      CART_EVENTS.forEach((name) => document.addEventListener(name, this.handleCartEvent));
      document.addEventListener('visibilitychange', this.handleVisibility);

      this.initialized = true;
      this.render();
      this.startTicking();
      if (this.config.threshold > 0) this.refreshCart();
    }

    disconnectedCallback() {
      if (!this.initialized) return;

      if (this.section) {
        this.section.removeEventListener('change', this.handleVariantSignal, true);
        this.section.removeEventListener('variant:change', this.handleVariantSignal, true);
      }
      document.removeEventListener('variant:change', this.handleDocumentVariant);
      CART_EVENTS.forEach((name) => document.removeEventListener(name, this.handleCartEvent));
      document.removeEventListener('visibilitychange', this.handleVisibility);

      this.stopTicking();
      clearTimeout(this.variantTimer);
      clearTimeout(this.cartTimer);
      this.cartController?.abort();
      this.cartController = null;
      this.initialized = false;
    }

    parsePayload() {
      const node = this.querySelector('[data-delivery-data]');
      if (!node) return {};
      try {
        const data = JSON.parse(node.textContent);
        return data && typeof data === 'object' ? data : {};
      } catch (error) {
        console.warn('[product-delivery] Invalid block data', error);
        return {};
      }
    }

    buildConfig(payload) {
      const { dataset } = this;
      const transitMin = clamp(toInt(dataset.transitMin, 3), 0, 60);
      const transitMax = Math.max(clamp(toInt(dataset.transitMax, transitMin), 0, 60), transitMin);
      const threshold = Number.parseFloat(dataset.freeThreshold);

      return {
        processingDays: clamp(toInt(dataset.processingDays, 1), 0, 60),
        transitMin,
        transitMax,
        cutoffMinutes: clamp(toInt(dataset.cutoffHour, 24), 0, 24) * 60,
        skipWeekends: dataset.skipWeekends === 'true',
        showCountdown: dataset.showCountdown === 'true',
        timeZone: this.resolveTimeZone(dataset.timezone),
        offsetMinutes: this.parseOffset(dataset.storeOffset),
        threshold: Number.isFinite(threshold) && threshold > 0 ? threshold : 0,
        holidays: this.parseHolidays(payload.holidays),
      };
    }

    resolveTimeZone(value) {
      const zone = (value ?? '').trim();
      if (!zone) return null;
      try {
        new Intl.DateTimeFormat('en-US', { timeZone: zone });
        return zone;
      } catch {
        console.warn(`[product-delivery] Unknown timezone "${zone}", falling back to store offset`);
        return null;
      }
    }

    parseOffset(value) {
      const match = OFFSET_PATTERN.exec((value ?? '').trim());
      if (!match) return -new Date().getTimezoneOffset();
      const minutes = Number(match[2]) * 60 + Number(match[3]);
      return match[1] === '-' ? -minutes : minutes;
    }

    parseHolidays(raw) {
      if (typeof raw !== 'string' || !raw.trim()) return new Set();
      return new Set(
        raw
          .split(/[\s,]+/)
          .map((entry) => entry.trim())
          .filter((entry) => DATE_PATTERN.test(entry) && !Number.isNaN(Date.parse(`${entry}T00:00:00Z`)))
      );
    }

    createDateFormatter() {
      const options = { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' };
      try {
        return new Intl.DateTimeFormat(this.locale, options);
      } catch {
        return new Intl.DateTimeFormat(undefined, options);
      }
    }

    createPartsFormatter() {
      if (!this.config.timeZone) return null;
      return new Intl.DateTimeFormat('en-US', {
        timeZone: this.config.timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hourCycle: 'h23',
      });
    }

    findForm() {
      if (!this.section) return null;
      const forms = [...this.section.querySelectorAll('form[action*="/cart/add"]')].filter(
        (form) => !form.closest(FORM_EXCLUDE) && form.querySelector('[name="id"]')
      );
      return forms.find((form) => /^(AddToCartForm|product-form)/i.test(form.id)) || forms[0] || null;
    }

    readVariantId() {
      const fromInput = this.form?.querySelector('[name="id"]')?.value;
      if (fromInput && this.variants[String(fromInput)]) return String(fromInput);

      const fromUrl = new URLSearchParams(window.location.search).get('variant');
      if (fromUrl && this.variants[fromUrl]) return fromUrl;

      return this.dataset.initialVariant || null;
    }

    setVariant(id) {
      if (!id || id === this.variantId) return;
      this.variantId = id;
      this.render();
    }

    handleVariantSignal(event) {
      const detailId = event?.detail?.variant?.id;
      if (detailId && this.variants[String(detailId)]) this.setVariant(String(detailId));
      clearTimeout(this.variantTimer);
      this.variantTimer = setTimeout(() => this.setVariant(this.readVariantId()), 0);
    }

    handleDocumentVariant(event) {
      if (event.target !== document) return;
      const detailId = event?.detail?.variant?.id;
      if (detailId && this.variants[String(detailId)]) this.setVariant(String(detailId));
    }

    getStoreNow() {
      const now = new Date();

      if (this.partsFormatter) {
        const parts = Object.fromEntries(
          this.partsFormatter
            .formatToParts(now)
            .filter((part) => part.type !== 'literal')
            .map((part) => [part.type, Number(part.value)])
        );
        return {
          date: new Date(Date.UTC(parts.year, parts.month - 1, parts.day)),
          minutes: (parts.hour % 24) * 60 + parts.minute,
          seconds: parts.second,
        };
      }

      const shifted = new Date(now.getTime() + this.config.offsetMinutes * 60000);
      return {
        date: new Date(Date.UTC(shifted.getUTCFullYear(), shifted.getUTCMonth(), shifted.getUTCDate())),
        minutes: shifted.getUTCHours() * 60 + shifted.getUTCMinutes(),
        seconds: shifted.getUTCSeconds(),
      };
    }

    isBusinessDay(date) {
      const day = date.getUTCDay();
      if (this.config.skipWeekends && (day === 0 || day === 6)) return false;
      return !this.config.holidays.has(toIso(date));
    }

    nextBusinessDay(date) {
      let cursor = addDays(date, 1);
      for (let i = 0; i < MAX_LOOKAHEAD && !this.isBusinessDay(cursor); i += 1) {
        cursor = addDays(cursor, 1);
      }
      return cursor;
    }

    addBusinessDays(date, count) {
      let cursor = date;
      let added = 0;
      let steps = 0;
      while (added < count && steps < MAX_LOOKAHEAD * 2) {
        cursor = addDays(cursor, 1);
        steps += 1;
        if (this.isBusinessDay(cursor)) added += 1;
      }
      return cursor;
    }

    computeSchedule() {
      const { date: today, minutes, seconds } = this.getStoreNow();
      const { cutoffMinutes, processingDays, transitMin, transitMax } = this.config;
      const beforeCutoff = this.isBusinessDay(today) && minutes < cutoffMinutes;
      const start = beforeCutoff ? today : this.nextBusinessDay(today);
      const ship = this.addBusinessDays(start, processingDays);

      return {
        beforeCutoff,
        secondsLeft: beforeCutoff ? (cutoffMinutes - minutes) * 60 - seconds : 0,
        earliest: this.addBusinessDays(ship, transitMin),
        latest: this.addBusinessDays(ship, transitMax),
      };
    }

    formatRange(start, end) {
      if (toIso(start) === toIso(end)) return this.dateFormatter.format(start);
      if (typeof this.dateFormatter.formatRange === 'function') {
        try {
          return this.dateFormatter.formatRange(start, end);
        } catch {
          return `${this.dateFormatter.format(start)} – ${this.dateFormatter.format(end)}`;
        }
      }
      return `${this.dateFormatter.format(start)} – ${this.dateFormatter.format(end)}`;
    }

    formatDuration(seconds) {
      const totalMinutes = Math.max(1, Math.ceil(seconds / 60));
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;
      return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
    }

    formatMoney(cents) {
      const currency = window.Shopify?.currency?.active || this.dataset.currency;
      if (!currency) return (cents / 100).toFixed(2);

      if (!this.moneyFormatters.has(currency)) {
        try {
          this.moneyFormatters.set(currency, new Intl.NumberFormat(this.locale, { style: 'currency', currency }));
        } catch {
          this.moneyFormatters.set(currency, null);
        }
      }

      const formatter = this.moneyFormatters.get(currency);
      return formatter ? formatter.format(cents / 100) : `${(cents / 100).toFixed(2)} ${currency}`;
    }

    presentmentThreshold() {
      if (!(this.config.threshold > 0)) return 0;
      const rate = Number.parseFloat(window.Shopify?.currency?.rate);
      return Math.round(this.config.threshold * 100 * (Number.isFinite(rate) && rate > 0 ? rate : 1));
    }

    setRow(row, visible) {
      if (row) row.hidden = !visible;
    }

    setText(element, text) {
      if (element && element.textContent !== text) element.textContent = text;
    }

    render() {
      if (!this.initialized) return;

      const variant = this.variants[this.variantId];
      const requiresShipping = variant?.requires_shipping !== false;
      const available = variant?.available !== false;
      const schedulable = requiresShipping && available;

      this.setRow(this.rows.digital, !requiresShipping);

      if (schedulable && (this.rows.range || this.rows.countdownText)) {
        const schedule = this.computeSchedule();

        if (this.rows.range) {
          this.setText(this.rows.range, this.formatRange(schedule.earliest, schedule.latest));
          this.setRow(this.rows.estimate, true);
        }

        const showCountdown =
          this.config.showCountdown &&
          Boolean(this.strings.countdown) &&
          Boolean(this.rows.countdownText) &&
          schedule.beforeCutoff &&
          schedule.secondsLeft > 0;

        if (showCountdown) {
          this.setText(
            this.rows.countdownText,
            fill(this.strings.countdown, {
              time: this.formatDuration(schedule.secondsLeft),
              date: this.dateFormatter.format(schedule.latest),
            })
          );
        }
        this.setRow(this.rows.countdown, showCountdown);
      } else {
        this.setRow(this.rows.estimate, false);
        this.setRow(this.rows.countdown, false);
      }

      this.renderFreeShipping(requiresShipping);
    }

    renderFreeShipping(requiresShipping) {
      if (!this.rows.free || !this.rows.freeText) return;

      const thresholdCents = this.presentmentThreshold();
      if (!requiresShipping || thresholdCents <= 0) {
        this.setRow(this.rows.free, false);
        return;
      }

      let text;
      if (this.cartTotal === null) {
        text = fill(this.strings.free, { amount: this.formatMoney(thresholdCents) });
      } else {
        const remaining = thresholdCents - this.cartTotal;
        text =
          remaining <= 0
            ? this.strings.freeQualified ?? ''
            : fill(this.strings.freeRemaining, { amount: this.formatMoney(remaining) });
      }

      this.setText(this.rows.freeText, text);
      this.setRow(this.rows.free, Boolean(text));
    }

    handleCartEvent(event) {
      if (!(this.config.threshold > 0)) return;
      const total = event?.detail?.cart?.total_price;
      if (Number.isFinite(total)) {
        this.cartTotal = total;
        this.render();
        return;
      }
      clearTimeout(this.cartTimer);
      this.cartTimer = setTimeout(() => this.refreshCart(), 300);
    }

    async refreshCart() {
      this.cartController?.abort();
      const controller = new AbortController();
      this.cartController = controller;
      const root = window.Shopify?.routes?.root || '/';

      try {
        const response = await fetch(`${root}cart.js`, {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin',
          signal: controller.signal,
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const cart = await response.json();
        if (Number.isFinite(cart?.total_price) && this.isConnected) {
          this.cartTotal = cart.total_price;
          this.render();
        }
      } catch (error) {
        if (error?.name !== 'AbortError') console.warn('[product-delivery] Cart fetch failed', error);
      } finally {
        if (this.cartController === controller) this.cartController = null;
      }
    }

    startTicking() {
      this.stopTicking();
      if (document.visibilityState === 'hidden') return;
      this.tickTimer = window.setInterval(this.render, TICK_MS);
    }

    stopTicking() {
      if (this.tickTimer) {
        window.clearInterval(this.tickTimer);
        this.tickTimer = null;
      }
    }

    handleVisibility() {
      if (document.visibilityState === 'visible') {
        this.render();
        this.startTicking();
      } else {
        this.stopTicking();
      }
    }
  }

  customElements.define('product-delivery', ProductDelivery);
})();